// src/stations/geometry/clawProtractor.ts — Interactive SVG Claw Protractor (FR-GEO-001)
// Direct touch-manipulation protractor with draggable lobster claw arm,
// live angle readout, classification (acute/right/obtuse/straight), and challenge mode.

import { tokens } from '@ui/designTokens';
import { SoundEngine } from '@audio/soundEngine';
import { ClaweyHud } from '@ui/components/claweyHud';
import { GeometryStorage } from '@storage/geometryStorage';
import { StationProgression } from '@storage/stationProgression';

export type AngleClassification =
  | 'Zero Angle'
  | 'Acute Angle'
  | 'Right Angle'
  | 'Obtuse Angle'
  | 'Straight Angle'
  | 'Reflex Angle';

export interface AngleChallenge {
  id: string;
  targetDegrees: number;
  prompt: string;
  targetType: AngleClassification;
  explanation: string;
}

export interface AngleChallengeSet {
  setNumber: number;
  title: string;
  theme: string;
  description: string;
  challenges: AngleChallenge[];
}

export const ANGLE_CHALLENGE_SETS: AngleChallengeSet[] = [
  {
    setNumber: 1,
    title: 'Cardinal Harbours',
    theme: 'Right & Straight Angles',
    description: 'Master the primary benchmarks: perfect 90° square corners and 180° straight lines.',
    challenges: [
      {
        id: 'ang-1-1',
        targetDegrees: 90,
        prompt: 'Set Clawey\'s claw to a <b>Right Angle</b> (exactly 90°)',
        targetType: 'Right Angle',
        explanation: 'A Right Angle forms a perfect square corner measuring exactly 90°.',
      },
      {
        id: 'ang-1-2',
        targetDegrees: 180,
        prompt: 'Flatten the claw completely to a <b>Straight Angle (180°)</b>',
        targetType: 'Straight Angle',
        explanation: 'A straight angle forms a flat straight line measuring exactly 180°.',
      },
      {
        id: 'ang-1-3',
        targetDegrees: 90,
        prompt: 'Align Clawey\'s claw with the <b>90° Perpendicular Pier</b>',
        targetType: 'Right Angle',
        explanation: 'Two perpendicular lines meet at an exact right angle of 90°.',
      },
    ],
  },
  {
    setNumber: 2,
    title: 'Sharp Rockpools',
    theme: 'Standard Acute Angles',
    description: 'Construct sharp acute angles strictly between 0° and 90°.',
    challenges: [
      {
        id: 'ang-2-1',
        targetDegrees: 45,
        prompt: 'Open the claw to a sharp <b>Acute Angle of 45°</b> (half of a right angle)',
        targetType: 'Acute Angle',
        explanation: '45° is an acute angle because it is greater than 0° and less than 90°.',
      },
      {
        id: 'ang-2-2',
        targetDegrees: 30,
        prompt: 'Form a narrow <b>Acute Angle of 30°</b> (one-third of a right angle)',
        targetType: 'Acute Angle',
        explanation: '30° is a narrow acute angle, like the pinch of a lobster\'s claw tips.',
      },
      {
        id: 'ang-2-3',
        targetDegrees: 60,
        prompt: 'Construct the interior corner of an Equilateral Triangle: <b>60°</b>',
        targetType: 'Acute Angle',
        explanation: 'Each internal angle of an equilateral triangle measures exactly 60° (60 + 60 + 60 = 180°).',
      },
    ],
  },
  {
    setNumber: 3,
    title: 'Broad Shallows',
    theme: 'Standard Obtuse Angles',
    description: 'Open Clawey\'s claw wide between 90° and 180°.',
    challenges: [
      {
        id: 'ang-3-1',
        targetDegrees: 135,
        prompt: 'Open the claw wide to an <b>Obtuse Angle of 135°</b>',
        targetType: 'Obtuse Angle',
        explanation: '135° is obtuse because it is greater than 90° and less than 180°.',
      },
      {
        id: 'ang-3-2',
        targetDegrees: 120,
        prompt: 'Open the claw to <b>120°</b> (the interior angle of a regular hexagon)',
        targetType: 'Obtuse Angle',
        explanation: 'A regular hexagon has six equal angles of 120°.',
      },
      {
        id: 'ang-3-3',
        targetDegrees: 150,
        prompt: 'Set the claw to a broad <b>Obtuse Angle of 150°</b>',
        targetType: 'Obtuse Angle',
        explanation: '150° is only 30° short of a flat straight line.',
      },
    ],
  },
  {
    setNumber: 4,
    title: 'Nautical Decades',
    theme: 'Multiples of 10°',
    description: 'Dial in exact multiples of 10° along Clawey\'s naturalist protractor scale.',
    challenges: [
      {
        id: 'ang-4-1',
        targetDegrees: 20,
        prompt: 'Set a narrow <b>20° Acute Angle</b> between Clawey\'s pincers',
        targetType: 'Acute Angle',
        explanation: '20° is an acute angle close to the baseline.',
      },
      {
        id: 'ang-4-2',
        targetDegrees: 70,
        prompt: 'Open the claw to <b>70°</b> (steep acute seabed reef incline)',
        targetType: 'Acute Angle',
        explanation: '70° is acute, exactly 20° shy of a right angle.',
      },
      {
        id: 'ang-4-3',
        targetDegrees: 110,
        prompt: 'Tilt the claw past the vertical to <b>110°</b>',
        targetType: 'Obtuse Angle',
        explanation: '110° is obtuse, 20° beyond a right angle.',
      },
      {
        id: 'ang-4-4',
        targetDegrees: 160,
        prompt: 'Open wide to <b>160°</b> (broad horizontal ledge)',
        targetType: 'Obtuse Angle',
        explanation: '160° is obtuse and 20° away from 180°.',
      },
    ],
  },
  {
    setNumber: 5,
    title: 'Naturalist Precision',
    theme: 'Multiples of 5°',
    description: 'Test your fine motor tactile control with 5° increments.',
    challenges: [
      {
        id: 'ang-5-1',
        targetDegrees: 25,
        prompt: 'Set the claw tip to a fine <b>25° angle</b>',
        targetType: 'Acute Angle',
        explanation: '25° is halfway between 20° and 30°.',
      },
      {
        id: 'ang-5-2',
        targetDegrees: 65,
        prompt: 'Measure the opening of a scallop shell: <b>65°</b>',
        targetType: 'Acute Angle',
        explanation: '65° is an acute angle halfway between 60° and 70°.',
      },
      {
        id: 'ang-5-3',
        targetDegrees: 115,
        prompt: 'Open the claw to a <b>115° obtuse angle</b>',
        targetType: 'Obtuse Angle',
        explanation: '115° is obtuse, between 110° and 120°.',
      },
      {
        id: 'ang-5-4',
        targetDegrees: 175,
        prompt: 'Flatten the claw almost straight: <b>175°</b>',
        targetType: 'Obtuse Angle',
        explanation: '175° is just 5° short of a straight angle!',
      },
    ],
  },
  {
    setNumber: 6,
    title: 'Benchmark Visuals',
    theme: 'Close Calls & Boundaries',
    description: 'Distinguish angles that are tantalizingly close to boundary lines.',
    challenges: [
      {
        id: 'ang-6-1',
        targetDegrees: 85,
        prompt: 'Set the claw to <b>85°</b> (looks like a square corner, but still acute!)',
        targetType: 'Acute Angle',
        explanation: 'Because 85° is less than 90°, it is strictly classified as an acute angle.',
      },
      {
        id: 'ang-6-2',
        targetDegrees: 95,
        prompt: 'Open just past a right angle to <b>95°</b> (barely obtuse!)',
        targetType: 'Obtuse Angle',
        explanation: 'Because 95° is greater than 90°, it is classified as obtuse.',
      },
      {
        id: 'ang-6-3',
        targetDegrees: 35,
        prompt: 'Set the claw to an acute angle of <b>35°</b>',
        targetType: 'Acute Angle',
        explanation: '35° is acute, exactly 10° less than 45°.',
      },
    ],
  },
  {
    setNumber: 7,
    title: 'Complementary Pairs',
    theme: 'Angles that Sum to 90°',
    description: 'Calculate and set complementary angles that combine to form 90°.',
    challenges: [
      {
        id: 'ang-7-1',
        targetDegrees: 50,
        prompt: 'Set the claw to the <b>complement of 40°</b> (90° - 40° = ?)',
        targetType: 'Acute Angle',
        explanation: '90° - 40° = 50°. Complementary angles sum to 90°.',
      },
      {
        id: 'ang-7-2',
        targetDegrees: 40,
        prompt: 'Set the claw to the <b>complement of 50°</b> (90° - 50° = ?)',
        targetType: 'Acute Angle',
        explanation: '90° - 50° = 40°. 40° + 50° = 90° (Right Angle).',
      },
      {
        id: 'ang-7-3',
        targetDegrees: 70,
        prompt: 'Set the claw to the <b>complement of 20°</b> (90° - 20° = ?)',
        targetType: 'Acute Angle',
        explanation: '90° - 20° = 70°. Together they make a full right angle.',
      },
    ],
  },
  {
    setNumber: 8,
    title: 'Supplementary Pairs',
    theme: 'Angles on a Straight Line (180°)',
    description: 'Calculate angles that sit on a straight line together.',
    challenges: [
      {
        id: 'ang-8-1',
        targetDegrees: 110,
        prompt: 'Set the angle supplementary to 70° on a straight line: <b>180° - 70° = ?</b>',
        targetType: 'Obtuse Angle',
        explanation: '180° - 70° = 110°. Angles on a straight line always sum to 180°.',
      },
      {
        id: 'ang-8-2',
        targetDegrees: 70,
        prompt: 'Set the angle supplementary to 110°: <b>180° - 110° = ?</b>',
        targetType: 'Acute Angle',
        explanation: '180° - 110° = 70° acute angle.',
      },
      {
        id: 'ang-8-3',
        targetDegrees: 130,
        prompt: 'Set the angle supplementary to 50°: <b>180° - 50° = ?</b>',
        targetType: 'Obtuse Angle',
        explanation: '180° - 50° = 130° obtuse angle.',
      },
    ],
  },
  {
    setNumber: 9,
    title: 'Geometric Polygons',
    theme: 'Interior Polygon Angles',
    description: 'Construct the internal corner angles of authentic geometric polygons.',
    challenges: [
      {
        id: 'ang-9-1',
        targetDegrees: 60,
        prompt: 'Construct the internal angle of an <b>Equilateral Triangle (60°)</b>',
        targetType: 'Acute Angle',
        explanation: 'All three internal angles of an equilateral triangle are 60° (180° ÷ 3 = 60°).',
      },
      {
        id: 'ang-9-2',
        targetDegrees: 90,
        prompt: 'Construct the internal corner of a <b>Square (90°)</b>',
        targetType: 'Right Angle',
        explanation: 'Each corner of a square or rectangle is 90° (360° ÷ 4 = 90°).',
      },
      {
        id: 'ang-9-3',
        targetDegrees: 120,
        prompt: 'Construct the internal angle of a <b>Regular Hexagon (120°)</b>',
        targetType: 'Obtuse Angle',
        explanation: 'A regular 6-sided hexagon has internal angles of 120° (720° ÷ 6 = 120°).',
      },
    ],
  },
  {
    setNumber: 10,
    title: 'Master Explorer Bearings',
    theme: 'Nautical Compass Bearings',
    description: 'Navigate by the three-figure bearings of HMS Challenger cartographers.',
    challenges: [
      {
        id: 'ang-10-1',
        targetDegrees: 45,
        prompt: 'Steer by the <b>North-East compass bearing: 045°</b>',
        targetType: 'Acute Angle',
        explanation: 'Bearing 045° points directly North-East.',
      },
      {
        id: 'ang-10-2',
        targetDegrees: 90,
        prompt: 'Steer due East on <b>Bearing 090°</b>',
        targetType: 'Right Angle',
        explanation: 'Bearing 090° is directly East, forming a 90° right angle from North.',
      },
      {
        id: 'ang-10-3',
        targetDegrees: 135,
        prompt: 'Steer South-East on <b>Bearing 135°</b>',
        targetType: 'Obtuse Angle',
        explanation: 'Bearing 135° points South-East.',
      },
      {
        id: 'ang-10-4',
        targetDegrees: 180,
        prompt: 'Steer due South on <b>Bearing 180°</b>',
        targetType: 'Straight Angle',
        explanation: 'Bearing 180° is directly opposite North on a straight line.',
      },
    ],
  },
];

export const ANGLE_CHALLENGES: AngleChallenge[] = ANGLE_CHALLENGE_SETS[0]!.challenges;

export class ClawProtractor {
  private container: HTMLElement;
  private currentDegrees: number = 45;
  private activeMode: 'explore' | 'challenge' = 'explore';
  private currentSetNumber: number = 1;
  private currentChallengeIndex: number = 0;
  private setScore: number = 0;
  private isDragging: boolean = false;
  private sound: SoundEngine;
  private claweyHud?: ClaweyHud;
  private streak: number = 0;
  private lastSoundAngle: number = 45;

  private get currentSet(): AngleChallengeSet {
    return ANGLE_CHALLENGE_SETS[this.currentSetNumber - 1] ?? ANGLE_CHALLENGE_SETS[0]!;
  }

  constructor(sound?: SoundEngine, claweyHud?: ClaweyHud) {
    this.container = document.createElement('div');
    this.container.className = 'claw-protractor-station';
    this.sound = sound || new SoundEngine();
    this.claweyHud = claweyHud;

    const prog = StationProgression.getProgression('geometry-angles');
    this.currentSetNumber = prog.currentSet;
  }

  render(): HTMLElement {
    this.updateView();
    return this.container;
  }

  private updateView(): void {
    const classification = ClawProtractor.classifyAngle(this.currentDegrees);
    const currentSet = this.currentSet;
    const challenge = currentSet.challenges[this.currentChallengeIndex] ?? currentSet.challenges[0];

    this.container.innerHTML = `
      <div style="background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 16px; padding: 24px; box-shadow: 0 6px 24px rgba(7, 20, 36, 0.08);">
        <!-- 10-Tier Set Navigation Carousel (FR-PROG-001) -->
        <div style="margin-bottom: 18px;">
          <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center;">
            <span>📐 Angle Missions · 10 Progressive Challenge Sets</span>
            <span style="color: ${tokens.color.morrisIndigo};">
              Mastered: ${StationProgression.getCompletedSetCount('geometry-angles')} of 10 Sets
            </span>
          </div>
          <div style="display: flex; gap: 8px; overflow-x: auto; padding: 4px 2px 10px; scrollbar-width: thin;">
            ${ANGLE_CHALLENGE_SETS.map((s) => {
              const isUnlocked = StationProgression.isSetUnlocked('geometry-angles', s.setNumber);
              const isCurrent = this.currentSetNumber === s.setNumber;
              const prog = StationProgression.getProgression('geometry-angles');
              const comp = prog.completedSets[s.setNumber];
              const starsStr = comp ? ' ' + '★'.repeat(comp.stars) : '';

              return `
                <button
                  class="touch-target protractor-set-pill-btn"
                  data-set-num="${s.setNumber}"
                  ${!isUnlocked ? 'disabled style="opacity: 0.45; cursor: not-allowed;"' : ''}
                  style="
                    padding: 8px 14px;
                    font-size: 0.85rem;
                    font-weight: 800;
                    border-radius: 20px;
                    white-space: nowrap;
                    border: 1.5px solid ${isCurrent ? tokens.color.gildedBrass : 'rgba(26,54,93,0.2)'};
                    background: ${isCurrent ? 'rgba(255, 246, 229, 0.95)' : '#FFFDF9'};
                    color: ${tokens.color.morrisIndigo};
                    cursor: ${isUnlocked ? 'pointer' : 'not-allowed'};
                  "
                >
                  ${!isUnlocked ? '🔒 ' : ''}Set ${s.setNumber}: ${s.title}${starsStr}
                </button>
              `;
            }).join('')}
          </div>
        </div>

        <!-- Mode Switcher & Stats Header -->
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; margin-bottom: 20px;">
          <div style="display: flex; gap: 10px;">
            <button id="btn-prot-explore" class="touch-target" style="padding: 10px 18px; font-size: 0.95rem; font-weight: 800; border-radius: 20px; border: 1.5px solid ${tokens.color.gildedBrass}; background: ${this.activeMode === 'explore' ? tokens.color.gildedBrass : '#FFFDF9'}; color: ${this.activeMode === 'explore' ? '#FFFDF9' : tokens.color.morrisIndigo};">
              🧭 Free Explore
            </button>
            <button id="btn-prot-challenge" class="touch-target" style="padding: 10px 18px; font-size: 0.95rem; font-weight: 800; border-radius: 20px; border: 1.5px solid ${tokens.color.gildedBrass}; background: ${this.activeMode === 'challenge' ? tokens.color.gildedBrass : '#FFFDF9'}; color: ${this.activeMode === 'challenge' ? '#FFFDF9' : tokens.color.morrisIndigo};">
              🎯 Claw Challenge
            </button>
          </div>

          <div style="display: flex; align-items: center; gap: 14px;">
            <span style="font-weight: 800; color: ${tokens.color.madderCoral}; font-size: 0.95rem;">
              🔥 Streak: ${this.streak}
            </span>
            <div style="padding: 6px 14px; border-radius: 12px; background: rgba(7, 20, 36, 0.08); font-size: 0.85rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">
              Snaps to 5°
            </div>
          </div>
        </div>

        ${
          this.activeMode === 'challenge' && challenge
            ? `
            <div style="background: rgba(255, 246, 229, 0.7); border-left: 4px solid ${tokens.color.gildedBrass}; padding: 14px 18px; border-radius: 10px; margin-bottom: 18px;">
              <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
                Angle Mission ${this.currentChallengeIndex + 1} of ${currentSet.challenges.length} · Set ${this.currentSetNumber}: ${currentSet.title}
              </div>
              <div style="font-size: 1.25rem; font-weight: 700; color: ${tokens.color.morrisIndigo}; margin: 4px 0 8px;">
                ${challenge.prompt}
              </div>
              <div style="font-size: 0.9rem; color: #3A4D66;">
                Tolerance: &plusmn;5&deg; · Drag Clawey's claw to target angle, then tap 'Check My Angle'
              </div>
            </div>
          `
            : ''
        }

        <!-- Live Angle Readout Badge -->
        <div style="display: flex; justify-content: center; align-items: center; gap: 16px; margin-bottom: 12px; flex-wrap: wrap;">
          <div style="background: #FFFDF9; border: 2px solid ${tokens.color.gildedBrass}; border-radius: 14px; padding: 10px 24px; text-align: center; min-width: 140px; box-shadow: 0 4px 12px rgba(229, 169, 60, 0.2);">
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">MEASURED ANGLE</div>
            <div id="protractor-degree-readout" style="font-size: 2.2rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; font-family: monospace;">
              ${this.currentDegrees}&deg;
            </div>
          </div>

          <div style="background: #FFFDF9; border: 1.5px solid rgba(7, 20, 36, 0.15); border-radius: 14px; padding: 10px 24px; text-align: center; min-width: 200px;">
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">CLASSIFICATION</div>
            <div id="protractor-class-readout" style="font-size: 1.35rem; font-weight: 800; color: ${this.getClassificationColor(classification)};">
              ${classification}
            </div>
          </div>
        </div>

        <!-- SVG Interactive Protractor Stage -->
        <div style="display: flex; justify-content: center; align-items: center; width: 100%; margin: 10px 0; touch-action: none;">
          ${this.generateProtractorSvg()}
        </div>

        <!-- Interactive Quick Presets & Verification -->
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; margin-top: 20px;">
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            <span style="font-size: 0.85rem; font-weight: 700; color: ${tokens.color.seaweedSage}; align-self: center;">Quick Snaps:</span>
            <button class="btn-snap-angle touch-target" data-deg="30" style="padding: 6px 12px; font-size: 0.85rem; border: 1px solid rgba(7, 20, 36, 0.2); border-radius: 12px; background: #FFFDF9;">30&deg;</button>
            <button class="btn-snap-angle touch-target" data-deg="45" style="padding: 6px 12px; font-size: 0.85rem; border: 1px solid rgba(7, 20, 36, 0.2); border-radius: 12px; background: #FFFDF9;">45&deg;</button>
            <button class="btn-snap-angle touch-target" data-deg="60" style="padding: 6px 12px; font-size: 0.85rem; border: 1px solid rgba(7, 20, 36, 0.2); border-radius: 12px; background: #FFFDF9;">60&deg;</button>
            <button class="btn-snap-angle touch-target" data-deg="90" style="padding: 6px 12px; font-size: 0.85rem; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 12px; background: rgba(255, 246, 229, 0.8); font-weight: 800;">90&deg;</button>
            <button class="btn-snap-angle touch-target" data-deg="120" style="padding: 6px 12px; font-size: 0.85rem; border: 1px solid rgba(7, 20, 36, 0.2); border-radius: 12px; background: #FFFDF9;">120&deg;</button>
            <button class="btn-snap-angle touch-target" data-deg="135" style="padding: 6px 12px; font-size: 0.85rem; border: 1px solid rgba(7, 20, 36, 0.2); border-radius: 12px; background: #FFFDF9;">135&deg;</button>
            <button class="btn-snap-angle touch-target" data-deg="180" style="padding: 6px 12px; font-size: 0.85rem; border: 1px solid rgba(7, 20, 36, 0.2); border-radius: 12px; background: #FFFDF9;">180&deg;</button>
          </div>

          ${
            this.activeMode === 'challenge'
              ? `
              <div style="display: flex; gap: 10px;">
                <button id="btn-check-angle" class="touch-target btn-primary" style="padding: 12px 28px; font-size: 1.05rem;">
                  Check My Angle &rarr;
                </button>
              </div>
            `
              : ''
          }
        </div>
      </div>
    `;

    this.attachListeners();
  }

  private generateProtractorSvg(): string {
    const cx = 250;
    const cy = 250;
    const r = 200;

    // Outer ticks and degree marks
    const ticks: string[] = [];
    for (let deg = 0; deg <= 180; deg += 5) {
      const rad = (deg * Math.PI) / 180;
      const isMajor = deg % 10 === 0;
      const isCard = deg === 0 || deg === 45 || deg === 90 || deg === 135 || deg === 180;
      const tickLen = isCard ? 16 : isMajor ? 12 : 6;

      const x1 = cx + (r - tickLen) * Math.cos(rad);
      const y1 = cy - (r - tickLen) * Math.sin(rad);
      const x2 = cx + r * Math.cos(rad);
      const y2 = cy - r * Math.sin(rad);

      ticks.push(`
        <line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}"
          stroke="${isCard ? tokens.color.morrisIndigo : '#7A8C9E'}"
          stroke-width="${isCard ? 2.5 : isMajor ? 1.5 : 1}" />
      `);

      if (isMajor) {
        const textRad = (deg * Math.PI) / 180;
        const tx = cx + (r - 28) * Math.cos(textRad);
        const ty = cy - (r - 28) * Math.sin(textRad) + 4;
        ticks.push(`
          <text x="${tx.toFixed(1)}" y="${ty.toFixed(1)}" font-size="${isCard ? '12' : '10'}" font-weight="${isCard ? '800' : '600'}" fill="${isCard ? tokens.color.morrisIndigo : '#5A6D86'}" text-anchor="middle" dominant-baseline="middle">
            ${deg}&deg;
          </text>
        `);
      }
    }

    // Claw arm calculation
    const radArm = (this.currentDegrees * Math.PI) / 180;
    const armLen = r + 10;
    const armX = cx + armLen * Math.cos(radArm);
    const armY = cy - armLen * Math.sin(radArm);

    // Dynamic shaded angle wedge
    const wedgeStartX = cx + 55 * Math.cos(0);
    const wedgeStartY = cy;
    const wedgeEndX = cx + 55 * Math.cos(radArm);
    const wedgeEndY = cy - 55 * Math.sin(radArm);
    const wedgePath =
      this.currentDegrees > 0
        ? `M ${cx} ${cy} L ${wedgeStartX} ${wedgeStartY} A 55 55 0 0 0 ${wedgeEndX.toFixed(1)} ${wedgeEndY.toFixed(1)} Z`
        : '';

    return `
      <svg id="claw-protractor-svg" viewBox="0 0 500 290" style="width: 100%; max-width: 540px; height: auto; user-select: none; -webkit-user-select: none;">
        <defs>
          <!-- Gilded Protractor Gradient -->
          <linearGradient id="prot-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#FFFDF9" />
            <stop offset="100%" stop-color="#FBF4E4" />
          </linearGradient>

          <filter id="prot-shadow" x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0" dy="6" stdDeviation="8" flood-color="rgba(7, 20, 36, 0.15)" />
          </filter>
        </defs>

        <!-- Semicircle Protractor Body -->
        <path d="M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy} Z"
          fill="url(#prot-gradient)"
          stroke="${tokens.color.gildedBrass}"
          stroke-width="3"
          filter="url(#prot-shadow)" />

        <!-- Inner clear cutout for naturalist alignment -->
        <path d="M ${cx - 70} ${cy} A 70 70 0 0 1 ${cx + 70} ${cy} Z"
          fill="#FFFDF9"
          stroke="rgba(229, 169, 60, 0.5)"
          stroke-width="1.5" />

        <!-- 90-degree reference line -->
        <line x1="${cx}" y1="${cy}" x2="${cx}" y2="${cy - r}" stroke="rgba(229, 169, 60, 0.4)" stroke-dasharray="4" stroke-width="1.5" />

        <!-- Ticks and Angle Numbers -->
        ${ticks.join('')}

        <!-- Interactive Shaded Angle Sector -->
        ${
          this.currentDegrees > 0
            ? `<path d="${wedgePath}" fill="rgba(255, 107, 74, 0.25)" stroke="${tokens.color.madderCoral}" stroke-width="1.5" />`
            : ''
        }

        <!-- Fixed Base Arm (0 degrees reference) -->
        <line x1="${cx}" y1="${cy}" x2="${cx + r + 15}" y2="${cy}" stroke="${tokens.color.morrisIndigo}" stroke-width="4.5" stroke-linecap="round" />
        <circle cx="${cx + r + 15}" cy="${cy}" r="4" fill="${tokens.color.gildedBrass}" />

        <!-- Pivot Center Anchor Pin -->
        <circle cx="${cx}" cy="${cy}" r="12" fill="${tokens.color.gildedBrass}" stroke="${tokens.color.morrisIndigo}" stroke-width="3" />
        <circle cx="${cx}" cy="${cy}" r="4" fill="#FFFDF9" />

        <!-- ROTATING CLAW ARM (Draggable) -->
        <g id="draggable-claw-arm" style="cursor: grab;">
          <!-- Arm Line -->
          <line x1="${cx}" y1="${cy}" x2="${armX.toFixed(1)}" y2="${armY.toFixed(1)}"
            stroke="${tokens.color.madderCoral}"
            stroke-width="5"
            stroke-linecap="round" />

          <!-- Lobster Claw Decorative Tip -->
          <g transform="translate(${armX.toFixed(1)}, ${armY.toFixed(1)}) rotate(${(this.currentDegrees - 90).toFixed(1)})">
            <!-- Lobster Pincer Graphic -->
            <path d="M -8 -4 C -14 -16, -4 -28, 0 -34 C 4 -28, 14 -16, 8 -4 Z" fill="${tokens.color.madderCoral}" stroke="${tokens.color.morrisIndigo}" stroke-width="2" />
            <path d="M -5 -18 C -18 -26, -12 -38, -2 -34" fill="none" stroke="${tokens.color.gildedBrass}" stroke-width="2" />
            <path d="M 5 -18 C 18 -26, 12 -38, 2 -34" fill="none" stroke="${tokens.color.gildedBrass}" stroke-width="2" />

            <!-- Large Touch Grab Handle (>= 48px target) -->
            <circle id="claw-drag-handle" cx="0" cy="-14" r="24" fill="rgba(229, 169, 60, 0.4)" stroke="${tokens.color.gildedBrass}" stroke-width="2.5" class="touch-target" style="cursor: grab;" />
            <circle cx="0" cy="-14" r="7" fill="#FFFDF9" />
          </g>
        </g>
      </svg>
    `;
  }

  private attachListeners(): void {
    // Set pill buttons (10-Tier Navigation)
    const setPills = this.container.querySelectorAll<HTMLButtonElement>('.protractor-set-pill-btn');
    setPills.forEach((btn) => {
      btn.addEventListener('click', () => {
        const setNum = parseInt(btn.getAttribute('data-set-num') || '1', 10);
        this.sound.playTap();
        this.currentSetNumber = setNum;
        this.currentChallengeIndex = 0;
        this.setScore = 0;
        this.activeMode = 'challenge';
        this.updateView();
      });
    });

    // Mode switcher buttons
    const btnExplore = this.container.querySelector<HTMLButtonElement>('#btn-prot-explore');
    btnExplore?.addEventListener('click', () => {
      this.sound.playTap();
      this.activeMode = 'explore';
      this.updateView();
    });

    const btnChallenge = this.container.querySelector<HTMLButtonElement>('#btn-prot-challenge');
    btnChallenge?.addEventListener('click', () => {
      this.sound.playTap();
      this.activeMode = 'challenge';
      this.updateView();
      if (this.claweyHud) {
        const currentSet = this.currentSet;
        const challenge = currentSet.challenges[this.currentChallengeIndex] ?? currentSet.challenges[0];
        this.claweyHud.say(
          `Challenge time! Set my claw to ${challenge?.targetDegrees} degrees. You've got this, Tudor!`,
          'thinking'
        );
      }
    });

    // Quick snap angle buttons
    const snapButtons = this.container.querySelectorAll<HTMLButtonElement>('.btn-snap-angle');
    snapButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const deg = parseInt(btn.getAttribute('data-deg') || '45', 10);
        this.setDegrees(deg);
        this.sound.playTap();
      });
    });

    // Check challenge answer button
    const btnCheck = this.container.querySelector<HTMLButtonElement>('#btn-check-angle');
    btnCheck?.addEventListener('click', () => {
      this.validateChallenge();
    });

    // SVG Dragging Interactions
    const svg = this.container.querySelector<SVGSVGElement>('#claw-protractor-svg');
    const dragArm = this.container.querySelector<SVGGElement>('#draggable-claw-arm');

    if (!svg || !dragArm) return;

    const handlePointerDown = (e: PointerEvent) => {
      e.preventDefault();
      this.isDragging = true;
      dragArm.style.cursor = 'grabbing';
      if (typeof dragArm.setPointerCapture === 'function') {
        dragArm.setPointerCapture(e.pointerId);
      }
      this.updateAngleFromPointer(e, svg);
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!this.isDragging) return;
      e.preventDefault();
      this.updateAngleFromPointer(e, svg);
    };

    const handlePointerUp = (e: PointerEvent) => {
      if (!this.isDragging) return;
      this.isDragging = false;
      dragArm.style.cursor = 'grab';
      if (typeof dragArm.releasePointerCapture === 'function') {
        dragArm.releasePointerCapture(e.pointerId);
      }
    };

    dragArm.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
  }

  private updateAngleFromPointer(e: PointerEvent, svg: SVGSVGElement): void {
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;

    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const svgP = pt.matrixTransform(ctm.inverse());

    const cx = 250;
    const cy = 250;
    const dx = svgP.x - cx;
    const dy = cy - svgP.y; // invert Y since SVG y goes down

    const rad = Math.atan2(dy, dx);
    let deg = rad * (180 / Math.PI);

    if (deg < 0) {
      // If pulled below horizontal baseline, snap to nearest 0 or 180
      deg = dx >= 0 ? 0 : 180;
    }

    // Snap to 5-degree increments
    const snapped = Math.round(deg / 5) * 5;
    const bounded = Math.max(0, Math.min(180, snapped));

    if (bounded !== this.currentDegrees) {
      this.setDegrees(bounded);

      // Play soft acoustic click every 15 degrees change
      if (Math.abs(bounded - this.lastSoundAngle) >= 15) {
        this.sound.playTap();
        this.lastSoundAngle = bounded;
      }
    }
  }

  /** Set degrees directly and update UI elements */
  setDegrees(deg: number): void {
    this.currentDegrees = Math.max(0, Math.min(180, deg));
    this.updateView();
  }

  getDegrees(): number {
    return this.currentDegrees;
  }

  /** Check current challenge against target degrees */
  validateChallenge(): boolean {
    const currentSet = this.currentSet;
    const challenge = currentSet.challenges[this.currentChallengeIndex];
    if (!challenge) return false;

    const diff = Math.abs(this.currentDegrees - challenge.targetDegrees);
    const isCorrect = diff <= 5; // ±5° tolerance

    GeometryStorage.recordAttempt({
      tool: 'protractor',
      targetValue: challenge.targetDegrees,
      actualValue: this.currentDegrees,
      correct: isCorrect,
      timestamp: Date.now(),
    });

    if (isCorrect) {
      this.streak += 1;
      this.setScore += 1;
      if (this.streak % 3 === 0) {
        this.sound.playStreak();
      } else {
        this.sound.playSuccess();
      }

      const isLastInSet = this.currentChallengeIndex >= currentSet.challenges.length - 1;

      if (this.claweyHud) {
        if (isLastInSet) {
          this.claweyHud.say(
            `Sensational, Tudor! You conquered Set ${this.currentSetNumber}: ${currentSet.title}! +25 Explorer Points awarded!`,
            'celebrating'
          );
        } else {
          this.claweyHud.say(
            `Magnificent! ${this.currentDegrees}° is ${challenge.targetType}! ${challenge.explanation}`,
            'celebrating'
          );
        }
      }

      if (isLastInSet) {
        StationProgression.completeSet(
          'geometry-angles',
          this.currentSetNumber,
          this.setScore,
          currentSet.challenges.length
        );
      }

      // Advance to next challenge or unlock next set
      setTimeout(() => {
        if (isLastInSet) {
          if (this.currentSetNumber < 10) {
            this.currentSetNumber += 1;
            this.currentChallengeIndex = 0;
            this.setScore = 0;
          } else {
            this.currentChallengeIndex = 0;
          }
        } else {
          this.currentChallengeIndex = (this.currentChallengeIndex + 1) % currentSet.challenges.length;
        }
        this.updateView();
      }, 1600);

      return true;
    } else {
      this.streak = 0;
      this.sound.playTryAgain();

      const nudge =
        this.currentDegrees < challenge.targetDegrees
          ? `Too narrow! Open my claw wider towards ${challenge.targetDegrees}°!`
          : `Too wide! Gently close my claw towards ${challenge.targetDegrees}°!`;

      if (this.claweyHud) {
        this.claweyHud.say(nudge, 'thinking');
      }

      return false;
    }
  }

  /** Static helper to classify any degree angle (KS2 Domain) */
  static classifyAngle(deg: number): AngleClassification {
    if (deg === 0) return 'Zero Angle';
    if (deg < 90) return 'Acute Angle';
    if (deg === 90) return 'Right Angle';
    if (deg < 180) return 'Obtuse Angle';
    if (deg === 180) return 'Straight Angle';
    return 'Reflex Angle';
  }

  private getClassificationColor(cls: AngleClassification): string {
    switch (cls) {
      case 'Right Angle':
        return tokens.color.madderCoral;
      case 'Acute Angle':
        return tokens.color.seaweedSage;
      case 'Obtuse Angle':
        return tokens.color.morrisIndigo;
      case 'Straight Angle':
        return tokens.color.gildedBrass;
      default:
        return tokens.color.morrisIndigo;
    }
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
